import { useState, useEffect } from "react";
import {
  ActionIcon, Card, Stack, Badge, Button, Group, Tooltip, TextInput,
  Drawer, Divider, Text, Table, Box, Collapse, Loader, Modal, Select, NumberInput,
} from "@mantine/core";
import {
  IconBrandWhatsapp, IconFileDownload, IconSend, IconX, IconEye,
  IconTruckDelivery, IconChecks, IconHistory, IconLock, IconSearch,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import {
  apiClient, useInfiniteResource, useAction, useResource,
  useReceivableLines, useReceiveAllPO, useClosePO, useReceiptHistory,
  useSetPoLineTax,
} from "../../../shared/api";
import {
  PageHeader, MoneyDisplay, EmptyState, InfiniteTable, formatQty, formatTRY,
  formatDateTime,
} from "../../../shared/ui";
import {
  GoodsReceiptFormModal, type PrefilledGrLine,
} from "../components/GoodsReceiptFormModal";

// Kural (tüm ekranlarda): SON adım (iş bitti) = YEŞİL, bir önceki adım = MAVİ.
const STATUS_COLOR: Record<string, string> = {
  DRAFT: "gray",
  SENT: "grape",         // gönderildi (erken adım)
  PARTIAL: "orange",
  RECEIVED: "blue",      // teslim alındı = COMPLETED'tan bir önceki adım → mavi
  COMPLETED: "green",    // tam teslim + faturalandı → SON adım (iş bitti) → yeşil
  CLOSED: "gray",        // manuel kapat (eksik/erken sonlandırma)
  CANCELLED: "red",
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: "Taslak",
  SENT: "Gönderildi",
  PARTIAL: "Kısmi Teslim",
  RECEIVED: "Teslim Alındı",
  COMPLETED: "Tamamlandı",
  CLOSED: "Kapatıldı",
  CANCELLED: "İptal",
};

export function PurchaseOrdersPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  // Arama (PO no / tedarikçi / RFQ no) + durum filtresi (backend filterset).
  const {
    items: list,
    total,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("purchase-orders", {
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(statusFilter ? { filters: { status: statusFilter } } : {}),
  });
  const send = useAction("purchase-orders", "send");
  const cancel = useAction("purchase-orders", "cancel");
  // Satır tıklaması / göz ikonuyla açılan detay drawer'ı.
  const [detail, setDetail] = useState<any | null>(null);
  // Başka sayfadan (örn. Mal Girişi → "PO'yu gör") highlight_id ile gelince
  // ilgili PO listeye düşünce detayını bir kez otomatik aç (GR sayfası deseni).
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("admin:nav:po");
      if (!raw) return;
      sessionStorage.removeItem("admin:nav:po");
      const params = JSON.parse(raw);
      if (typeof params.highlight_id === "string") setHighlightId(params.highlight_id);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (autoOpened || detail || !highlightId) return;
    const match = list.find((p) => p.id === highlightId);
    if (match) { setDetail(match); setAutoOpened(true); }
  }, [list, highlightId, autoOpened, detail]);

  async function doSend(id: string) {
    try {
      await send.mutateAsync({ id });
      notifications.show({ message: "PO gönderildi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader title="Satın Alma Siparişleri (PO)" subtitle={`${total} sipariş`} />
      <Group>
        <TextInput
          placeholder="PO no, tedarikçi veya RFQ no ile ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 400 }}
        />
        <Select
          placeholder="Tüm durumlar"
          clearable
          value={statusFilter}
          onChange={setStatusFilter}
          data={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
          style={{ width: 200 }}
        />
      </Group>
      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={list}
          rowKey={(p) => p.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onRowClick={(p) => setDetail(p)}
          emptyContent={<EmptyState title="PO yok" />}
          columns={[
            { header: "PO No", render: (p) => <span style={{ fontWeight: 600 }}>{p.po_number}</span> },
            { header: "RFQ No", render: (p) => p.source_rfq_number || <Text c="dimmed" size="sm">—</Text> },
            { header: "Tedarikçi", render: (p) => p.supplier_name },
            {
              header: "Durum",
              render: (p) => (
                <Group gap={4} wrap="nowrap">
                  <Badge color={STATUS_COLOR[p.status]}>{STATUS_LABEL[p.status] || p.status}</Badge>
                  {/* Bu PO'ya bağlı, henüz onaylanmamış (DRAFT) mal girişi varsa
                      PO status'ü değişmese de kullanıcıya onay beklediğini göster. */}
                  {p.has_pending_receipt && (
                    <Tooltip label="Bu PO için oluşturulan mal girişi onay bekliyor — Mal Girişleri ekranından onaylayın">
                      <Badge color="orange" variant="light" leftSection={<IconChecks size={12} />}>
                        Mal Girişi Onayı Bekliyor
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
              ),
            },
            { header: "Tarih", render: (p) => formatDateTime(p.ordered_at || p.created_at) },
            { header: "Toplam", align: "right", render: (p) => <MoneyDisplay minor={p.total_minor} fw={600} /> },
            {
              header: "İşlem",
              render: (p) => (
                // Satır tıklaması da detayı açar; aksiyon ikonları olayı
                // satıra taşımasın (stopPropagation).
                <Group gap="xs" wrap="nowrap">
                  <Tooltip label="Detayı gör">
                    <ActionIcon
                      variant="subtle"
                      onClick={(e) => { e.stopPropagation(); setDetail(p); }}
                      aria-label="Detayı gör"
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="PDF indir">
                    <ActionIcon
                      variant="subtle"
                      onClick={(e) => { e.stopPropagation(); downloadPoPdf(p.id, p.po_number); }}
                      aria-label="PDF indir"
                    >
                      <IconFileDownload size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="WhatsApp ile tedarikçiye gönder">
                    <ActionIcon
                      variant="subtle"
                      color="green"
                      onClick={(e) => { e.stopPropagation(); sharePoWhatsApp(p.id); }}
                      aria-label="WhatsApp"
                    >
                      <IconBrandWhatsapp size={16} />
                    </ActionIcon>
                  </Tooltip>
                  {p.status === "DRAFT" && (
                    <Button size="xs" leftSection={<IconSend size={14} />} onClick={(e) => { e.stopPropagation(); doSend(p.id); }}>
                      Gönder
                    </Button>
                  )}
                  {!["CANCELLED", "RECEIVED", "CLOSED"].includes(p.status) && (
                    <Button size="xs" color="red" variant="light" leftSection={<IconX size={14} />} onClick={(e) => { e.stopPropagation(); cancel.mutateAsync({ id: p.id, reason: "İptal" }); }}>
                      İptal
                    </Button>
                  )}
                </Group>
              ),
            },
          ]}
        />
      </Card>

      <PurchaseOrderDetailDrawer po={detail} onClose={() => setDetail(null)} />
    </Stack>
  );
}

// Satır teslim durumu: ✓ Tamamlandı / 📦 Kısmi / ⏳ Bekliyor.
function lineStatusChip(ordered: number, received: number) {
  if (received >= ordered && ordered > 0) {
    return <Badge size="sm" color="green" variant="light">✓ Tamamlandı ({formatQty(received)}/{formatQty(ordered)})</Badge>;
  }
  if (received > 0) {
    return <Badge size="sm" color="orange" variant="light">📦 Kısmi ({formatQty(received)}/{formatQty(ordered)})</Badge>;
  }
  return <Badge size="sm" color="gray" variant="light">⏳ Bekliyor (0/{formatQty(ordered)})</Badge>;
}

// PO'nun ne sipariş edildiğini gösteren detay drawer'ı + mal girişi köprüsü.
// Export: Tedarikçi detayı "Satın Alma" sekmesinden de açılır.
export function PurchaseOrderDetailDrawer({
  po: poProp, onClose,
}: { po: any | null; onClose: () => void }) {
  // Drawer açıkken PO'yu TAZE çek — liste satırından gelen kopya stale kalır
  // (KDV güncelleyince toplam yansımaz). Taze veri yoksa prop'a düş.
  const fresh = useResource<any>(
    "purchase-orders",
    { id: poProp?.id, enabled: !!poProp?.id },
  );
  const po = (poProp?.id && fresh.data) ? fresh.data : poProp;
  const lines: any[] = po?.lines || [];
  const canReceive = po && ["SENT", "PARTIAL"].includes(po.status);
  // KDV yalnız DRAFT/SENT iken düzenlenebilir (tam teslim alınmadan).
  const canEditTax = po && ["DRAFT", "SENT"].includes(po.status);

  // PO'dan mal girişi: receivable-lines çek, GR formunu prefilled aç.
  const [grPrefill, setGrPrefill] = useState<any | null>(null);
  const [receiveAllOpen, setReceiveAllOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const receivable = useReceivableLines(canReceive ? po?.id : null);
  const history = useReceiptHistory(historyOpen ? po?.id : null);
  const closePo = useClosePO();
  const setTax = useSetPoLineTax();
  // KDV oranı yerel taslak — kullanıcı yazarken her tuşta API'ye gitmesin;
  // yalnız odak kaybında (onBlur) ve değer gerçekten değiştiyse gönderilir.
  const [taxDraft, setTaxDraft] = useState<Record<string, number>>({});

  function commitLineTax(line: any) {
    if (!po) return;
    const draft = taxDraft[line.id];
    if (draft == null || Number.isNaN(draft)) return;
    const current = Number(line.tax_rate_percent || 0);
    if (draft === current) return; // değişmediyse istek atma
    setTax.mutate(
      { poId: po.id, poLineId: line.id, taxRatePercent: draft },
      {
        onError: (e: any) =>
          notifications.show({ message: e?.response?.data?.detail || "KDV güncellenemedi", color: "red" }),
      },
    );
  }

  const histRows: any[] = history.data?.rows || [];

  function openGoodsReceipt() {
    const rl = receivable.data;
    if (!rl || !rl.lines?.length) {
      notifications.show({ message: "Teslim alınacak kalan kalem yok", color: "orange" });
      return;
    }
    setGrPrefill({
      supplierId: rl.supplier_id,
      supplierName: rl.supplier_name,
      purchaseOrderId: rl.po_id,
      lines: rl.lines as PrefilledGrLine[],
    });
  }

  function doClose() {
    modals.openConfirmModal({
      title: "PO'yu Kapat (kalanı iptal)",
      children: (
        <Text size="sm">
          Gelmeyen kalemler "teslim edilmedi" olarak kalacak; PO kapanacak.
          Stok ve cari yalnız gelen mal kadar işlendi — değişmez. Onaylıyor musunuz?
        </Text>
      ),
      labels: { confirm: "PO'yu Kapat", cancel: "Vazgeç" },
      confirmProps: { color: "teal" },
      onConfirm: async () => {
        try {
          await closePo.mutateAsync({ poId: po.id, reason: "Kalan kalemler iptal" });
          notifications.show({ message: "PO kapatıldı", color: "green" });
          onClose();
        } catch (e: any) {
          notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
        }
      },
    });
  }

  return (
    <>
    <Drawer
      opened={!!po}
      onClose={onClose}
      position="right"
      size="xl"
      padding="lg"
      title={
        <Group gap="sm">
          <Text fw={700} size="lg">{po?.po_number}</Text>
          {po && (
            <Badge color={STATUS_COLOR[po.status]}>
              {STATUS_LABEL[po.status] || po.status}
            </Badge>
          )}
          {po?.has_pending_receipt && (
            <Tooltip label="Bu PO için oluşturulan mal girişi onay bekliyor — Mal Girişleri ekranından onaylayın">
              <Badge color="orange" variant="light" leftSection={<IconChecks size={12} />}>
                Mal Girişi Onayı Bekliyor
              </Badge>
            </Tooltip>
          )}
        </Group>
      }
    >
      {po && (
        <Stack gap="md">
          <Group gap="xl">
            <Box>
              <Text size="xs" c="dimmed">Tedarikçi</Text>
              <Text fw={500}>{po.supplier_name || "—"}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Tarih</Text>
              <Text fw={500}>{formatDateTime(po.ordered_at || po.created_at)}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Toplam</Text>
              <MoneyDisplay minor={po.total_minor} fw={600} />
            </Box>
          </Group>

          {/* Mal girişi köprüsü — yarım/gönderilmiş PO'dan teslim alma */}
          {canReceive && (
            <Group gap="sm">
              <Button
                leftSection={<IconTruckDelivery size={16} />}
                onClick={openGoodsReceipt}
                loading={receivable.isLoading}
              >
                Mal Girişi Yap
              </Button>
              <Button
                variant="light"
                color="green"
                leftSection={<IconChecks size={16} />}
                onClick={() => setReceiveAllOpen(true)}
              >
                Tamamını Teslim Al
              </Button>
              <Button
                variant="light"
                color="teal"
                leftSection={<IconLock size={16} />}
                onClick={doClose}
              >
                PO'yu Kapat
              </Button>
            </Group>
          )}

          <Divider label="Sipariş Kalemleri" labelPosition="left" />

          {lines.length === 0 ? (
            <EmptyState title="Bu siparişte kalem yok" />
          ) : (
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Hammadde</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Sipariş</Table.Th>
                  <Table.Th>Durum</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Birim Fiyat (net)</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>KDV %</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Tutar (net)</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {lines.map((l) => {
                  const lineTotal =
                    Number(l.quantity_ordered || 0)
                    * Number(l.unit_price_minor || 0);
                  return (
                    <Table.Tr key={l.id}>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm">
                            {l.ingredient_name || l.supply_item_name || "(adsız)"}
                          </Text>
                          {l.supply_item_name && !l.ingredient_name && (
                            <Badge size="xs" variant="light" color="teal">Mutfak-dışı</Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        {formatQty(l.quantity_ordered)}{l.unit ? ` ${l.unit}` : ""}
                      </Table.Td>
                      <Table.Td>
                        {lineStatusChip(Number(l.quantity_ordered || 0), Number(l.quantity_received || 0))}
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        {formatTRY(l.unit_price_minor)}
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        {canEditTax ? (
                          <NumberInput
                            size="xs"
                            value={taxDraft[l.id] ?? Number(l.tax_rate_percent || 0)}
                            min={0}
                            max={100}
                            w={80}
                            suffix="%"
                            onChange={(v) => setTaxDraft((d) => ({ ...d, [l.id]: Number(v) }))}
                            onBlur={() => commitLineTax(l)}
                            onKeyDown={(e) => { if (e.key === "Enter") commitLineTax(l); }}
                            disabled={setTax.isPending}
                          />
                        ) : (
                          <Text size="sm">%{Number(l.tax_rate_percent || 0)}</Text>
                        )}
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right", fontWeight: 600 }}>
                        {formatTRY(lineTotal)}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
              <Table.Tfoot>
                <Table.Tr>
                  <Table.Th colSpan={5} style={{ textAlign: "right" }}>Ara Toplam (net)</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>
                    <MoneyDisplay minor={po.subtotal_minor ?? po.total_minor} />
                  </Table.Th>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th colSpan={5} style={{ textAlign: "right" }}>KDV</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>
                    <MoneyDisplay minor={po.tax_minor ?? 0} />
                  </Table.Th>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th colSpan={5} style={{ textAlign: "right" }}>Genel Toplam (brüt)</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>
                    <MoneyDisplay minor={po.total_minor} fw={700} />
                  </Table.Th>
                </Table.Tr>
              </Table.Tfoot>
            </Table>
          )}

          {/* Teslimat Geçmişi — parça parça gelen sevkiyatlar + fiyat farkı */}
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconHistory size={14} />}
            onClick={() => setHistoryOpen((v) => !v)}
          >
            Teslimat Geçmişi {historyOpen ? "▲" : "▼"}
          </Button>
          <Collapse in={historyOpen}>
            <ReceiptHistoryTable loading={history.isLoading} rows={histRows} />
          </Collapse>
        </Stack>
      )}
    </Drawer>

    {/* PO'dan prefilled mal girişi formu */}
    <GoodsReceiptFormModal
      opened={!!grPrefill}
      onClose={() => setGrPrefill(null)}
      prefill={grPrefill}
    />

    {/* Tamamını teslim al — depo seçimli onay */}
    {po && (
      <ReceiveAllModal
        po={receiveAllOpen ? po : null}
        onClose={() => setReceiveAllOpen(false)}
        onDone={onClose}
      />
    )}
    </>
  );
}

// Teslimat geçmişi tablosu — her parti satır satır, fiyat farkı rozetli.
function ReceiptHistoryTable({ loading, rows }: { loading: boolean; rows: any[] }) {
  if (loading) return <Group justify="center" py="sm"><Loader size="sm" /></Group>;
  if (!rows.length) return <Text size="xs" c="dimmed" py="sm">Henüz teslimat yok.</Text>;
  return (
    <Table striped withTableBorder fz="xs">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>İrsaliye</Table.Th>
          <Table.Th>Tarih</Table.Th>
          <Table.Th>Kalem</Table.Th>
          <Table.Th ta="right">Miktar</Table.Th>
          <Table.Th ta="right">Teslim Fiyat</Table.Th>
          <Table.Th ta="right">PO Fiyat</Table.Th>
          <Table.Th ta="right">Fark</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((r, i) => {
          const poP = r.po_unit_price_minor;
          const recv = r.unit_price_minor;
          let diffBadge = <Text c="dimmed">—</Text>;
          if (poP != null && poP > 0) {
            const pct = ((recv - poP) / poP) * 100;
            if (Math.abs(pct) >= 0.5) {
              const up = pct > 0;
              diffBadge = (
                <Badge size="sm" color={up ? "red" : "green"} variant="light">
                  {up ? "▲ +" : "▼ "}{pct.toFixed(1)}%
                </Badge>
              );
            } else {
              diffBadge = <Badge size="sm" color="gray" variant="light">=</Badge>;
            }
          }
          return (
            <Table.Tr key={`${r.gr_id}-${i}`}>
              <Table.Td>{r.gr_number}</Table.Td>
              <Table.Td>{r.received_at ? formatDateTime(r.received_at) : "—"}</Table.Td>
              <Table.Td>{r.item_name}</Table.Td>
              <Table.Td ta="right">{formatQty(r.quantity_received)}</Table.Td>
              <Table.Td ta="right">{formatTRY(recv)}</Table.Td>
              <Table.Td ta="right">{poP != null ? formatTRY(poP) : "—"}</Table.Td>
              <Table.Td ta="right">{diffBadge}</Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

// "Tamamını Teslim Al" — depo seç + onayla → receive-all (PO fiyatıyla).
function ReceiveAllModal({
  po, onClose, onDone,
}: { po: any | null; onClose: () => void; onDone: () => void }) {
  const { data: locationsData } = useResource<any>("stock-locations");
  const locations: any[] = locationsData?.results || locationsData || [];
  const receiveAll = useReceiveAllPO();
  const [locationId, setLocationId] = useState<string | null>(null);
  // İrsaliye geldiyse tedarikçinin irsaliye numarası — "Mal Girişi Yap"
  // formundaki alanın aynısı; opsiyonel.
  const [deliveryNote, setDeliveryNote] = useState("");

  async function confirm() {
    if (!locationId) {
      notifications.show({ message: "Depo seçin", color: "orange" });
      return;
    }
    try {
      await receiveAll.mutateAsync({
        poId: po.id,
        location: locationId,
        delivery_note_number: deliveryNote.trim(),
      });
      notifications.show({
        message: "Taslak mal kabul oluşturuldu — Mal Girişleri'nden 'Onayla' deyince stok/cari işlenir",
        color: "green",
      });
      onClose();
      onDone();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Modal opened={!!po} onClose={onClose} title="Tamamını Teslim Al" size="md">
      <Stack gap="md">
        <Text size="sm">
          PO'nun kalan tüm kalemleri <b>PO fiyatıyla</b> bir <b>taslak mal kabule</b>
          alınacak. Stok ve cari HEMEN işlenmez — Mal Girişleri'nde
          <b>"Onayla"</b> dendiğinde işlenir (dört göz: kabul eden ≠ onaylayan
          gözden geçirir). Fiyat/miktar farkı varsa "Mal Girişi Yap" ile satır satır girin.
        </Text>
        <Select
          label="Depo / Lokasyon"
          placeholder="Depo seç"
          required
          data={locations.map((l) => ({ value: l.id, label: l.name }))}
          value={locationId}
          onChange={setLocationId}
          searchable
        />
        <TextInput
          label="İrsaliye No"
          placeholder="Tedarikçinin irsaliye numarası (opsiyonel)"
          value={deliveryNote}
          onChange={(e) => setDeliveryNote(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Vazgeç</Button>
          <Button color="green" onClick={confirm} loading={receiveAll.isPending}>
            Taslak Mal Kabul Oluştur
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ============================================================
// PDF + WhatsApp helpers (Sprint 35)
// ============================================================
async function downloadPoPdf(poId: string, poNumber: string) {
  try {
    const response = await apiClient.get(
      `/api/v1/purchase-orders/${poId}/pdf/`,
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = `PO-${poNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    notifications.show({ message: "PDF indirildi", color: "green" });
  } catch (e: any) {
    notifications.show({
      message: e?.response?.data?.detail || "PDF üretilemedi",
      color: "red",
    });
  }
}

async function sharePoWhatsApp(poId: string) {
  try {
    const res = await apiClient.get(
      `/api/v1/purchase-orders/${poId}/whatsapp-share/`,
    );
    window.open(res.data.whatsapp_url, "_blank", "noopener,noreferrer");
    if (!res.data.supplier_phone) {
      notifications.show({
        title: "Tedarikçi telefonu yok",
        message: "Supplier sayfasından telefon ekleyin. Mesaj hazır geldi.",
        color: "yellow",
      });
    }
  } catch (e: any) {
    notifications.show({
      message: e?.response?.data?.detail || "WhatsApp linki üretilemedi",
      color: "red",
    });
  }
}
